import React, { useState, useMemo, useContext, useRef } from 'react';
import { updateDoc, doc, getFirestore, Timestamp, getDoc, getDocs, collection, setDoc } from 'firebase/firestore';
import { AppContext } from './AppContext'; // Adjust the path as needed

const SongItem = ({ item, onPress, editControlsStyle, calendarPopupStyle, confirmButtonStyle, compactEditor }) => {
  const { userId2 } = useContext(AppContext); // Access userId2 from AppContext
  const firestore = getFirestore();
  const artworkUrl = item?.ArtworkURL || item?.artworkURL;
  const [isEditing, setIsEditing] = useState(false);
  const dateInputRef = useRef(null); // Ref to the date input field

  const isDat = !!(item?.isNFT || item?.editions || item?.tier);

  // Helpers to read the existing timestamp safely and preserve its type
  const toMillis = (v) =>
    v == null
      ? Date.now()
      : typeof v === 'number'
      ? v
      : typeof v === 'string'
      ? (Date.parse(v) || Date.now())
      : (v?.seconds != null)
      ? v.seconds * 1000 + Math.floor((v.nanoseconds || 0) / 1e6)
      : typeof v?.toMillis === 'function'
      ? v.toMillis()
      : v instanceof Date
      ? v.getTime()
      : typeof v?.toDate === 'function'
      ? v.toDate().getTime()
      : Date.now();

  const detectType = (v) =>
    v?.seconds != null
      ? 'fs'
      : typeof v === 'number'
      ? 'num'
      : typeof v === 'string'
      ? 'str'
      : v instanceof Date
      ? 'date'
      : typeof v?.toDate === 'function' || typeof v?.toMillis === 'function'
      ? 'fs'
      : 'unknown';

  // Initialize date and time in local format (robust to Timestamp/string/number/Date)
  const originalTsType = detectType(item?.releaseTimestamp);
  const localDate = item?.releaseTimestamp ? new Date(toMillis(item.releaseTimestamp)) : new Date();
  const [date, setDate] = useState(localDate.toISOString().split('T')[0]);

  const mandatoryFollowingIds = [
    "h0fEIStkM4XHTDErZM5i8XIo9rA3",
    "OHaY20b54nTGd5BBgbJOL2BHEbX2",
    "rdX289s3YBP31Hw5qn2eXVywtBq1",
    "Mkf4631LCEOYmHXWRzGirPdxBrt1"
  ];

  const handleConfirm = async (e) => {
    e.stopPropagation(); // Prevent the event from propagating to the parent element

    // Preserve the original time and only update the date portion
    const updatedDate = new Date(date);
    updatedDate.setUTCHours(
      localDate.getUTCHours(),
      localDate.getUTCMinutes(),
      localDate.getUTCSeconds(),
      localDate.getUTCMilliseconds()
    );

    // Preserve the existing type in the summaries so live sorting remains unchanged
    const newSummaryValue =
      originalTsType === 'fs'
        ? Timestamp.fromDate(updatedDate)
        : originalTsType === 'num'
        ? updatedDate.getTime()
        : originalTsType === 'str'
        ? updatedDate.toISOString()
        : Timestamp.fromDate(updatedDate);

    try {
      if (isDat) {
        // Update assetsSummary/main array entry (DATs)
        const contentId = item.contentId || item.id;
        const sumRef = doc(firestore, 'assetsSummary', 'main');
        const sumSnap = await getDoc(sumRef);
        if (sumSnap.exists()) {
          const data = sumSnap.data() || {};
          const arr = Array.isArray(data.assets) ? data.assets : [];
          const next = arr.map(a => (a.id === contentId ? { ...a, releaseTimestamp: newSummaryValue } : a));
          await setDoc(sumRef, { assets: next }, { merge: true });
        }
      } else {
        // Update musicSummaries/summary_* posts[] entry (regular songs)
        const songId = item.id;
        const snaps = await getDocs(collection(firestore, 'musicSummaries'));
        for (const d of snaps.docs) {
          const data = d.data() || {};
          const posts = Array.isArray(data.posts) ? data.posts : [];
          if (posts.some(p => p?.id === songId)) {
            const next = posts.map(p => (p.id === songId ? { ...p, releaseTimestamp: newSummaryValue } : p));
            await setDoc(d.ref, { posts: next }, { merge: true });
            break;
          }
        }
      }

      // Update the local date state to reflect the new date
      setDate(updatedDate.toISOString().split('T')[0]);
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating releaseTimestamp:", error);
    }
  };

  // Selecting a date should choose it and close the calendar.
  // Use a deferred blur so browsers commit the value first.
  const handleDatePick = (e) => {
    const v = e.target.value;
    setDate(v);
    setTimeout(() => {
      dateInputRef.current?.blur();
    }, 0);
  };

  const formatArtistFeatures = (artistFeatures) => {
    return artistFeatures.map(feature => {
      const parts = feature.tag.split(' - ');
      return parts.length > 1 ? parts[1] : parts[0];
    }).join(', ');
  };
  
  const songDisplayName = useMemo(() => {
    return item.isAlbum
      ? item.albumName || 'Unknown Album'
      : `${item.SongName || 'Unknown Song'}${item.artistFeatures && item.artistFeatures.length > 0 ? ` ft. ${formatArtistFeatures(item.artistFeatures)}` : ''}`;
  }, [item]);
  

  if (!item) {
    return <div style={styles.songArtworkPlaceholder}>Loading...</div>;
  }

  return (
    <div style={styles.itemContainer} onClick={() => onPress(item)}>
      <div style={styles.horizontalTouchable}>
        {artworkUrl ? (
          <img src={artworkUrl} alt="Artwork" style={styles.songArtwork} />
        ) : (
          <div style={styles.songArtworkPlaceholder}>Loading...</div>
        )}
      </div>
      <div style={styles.artistName}>{item.ArtistName}</div>
      <div style={styles.songName}>{songDisplayName}</div>

      {mandatoryFollowingIds.includes(userId2) && (
        <div style={styles.timestampContainer}>
          {!isEditing ? (
            <>
              <div style={styles.timestampText}>
                Release Date: {localDate.toLocaleDateString()} {/* Display only the date */}
              </div>
              <button style={styles.editButton} onClick={(e) => { e.stopPropagation(); setIsEditing(true); }}>
                Edit
              </button>
            </>
          ) : (
            <div style={styles.editContainer}>
<input
  type="text"
  placeholder="YYYY-MM-DD"
  value={date}
  onChange={(e) => {
    const v = e.target.value;
    // Validate format while allowing full year typing
    if (/^\d{0,4}(-\d{0,2}){0,2}$/.test(v)) setDate(v);
  }}
  onBlur={() => {
    // If year-only typed, fill with current month/day
    if (/^\d{4}$/.test(date)) {
      const current = new Date();
      setDate(`${date}-${String(current.getMonth() + 1).padStart(2, '0')}-${String(current.getDate()).padStart(2, '0')}`);
    }
  }}
  ref={dateInputRef}
  style={styles.dateInput}
  onClick={(e) => e.stopPropagation()}
/>

              <button style={styles.confirmButton} onClick={handleConfirm}>Confirm</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const styles = {
  itemContainer: {
    width: '100%',            // fills the 200 px wrapper
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    cursor: 'pointer',
  },
  
  horizontalTouchable: {
    width: '100px',
    height: '100px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'transparent',
  },
  songArtwork: {
    width: '100%',
    height: '100%',
    borderRadius: '8px',
  },
  songArtworkPlaceholder: {
    width: '100%',
    height: '100%',
    borderRadius: '8px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#cccccc',
  },
  artistName: {
    marginTop: '8px',
    color: 'white',
    fontSize: '13px',
    fontFamily: 'brhendrix',
    fontWeight: 'bold',
    textAlign: 'left',
    whiteSpace: 'normal',           // allow wrapping
    wordBreak: 'break-word',
  },
  songName: {
    marginTop: '2px',
    color: '#b8b8b8',
    fontFamily: 'brhendrix',
    fontSize: '10px',
    textAlign: 'left',
    whiteSpace: 'normal', // Allow wrapping
    wordWrap: 'break-word',
  },
  timestampContainer: {
    marginTop: '10px',
    color: '#b8b8b8',
    fontFamily: 'brhendrix',
    fontSize: '8px',
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
  },
  timestampText: {
    marginBottom: '5px',
  },
  editButton: {
    backgroundColor: '#62fbfa',
    border: 'none',
    padding: '5px',
    cursor: 'pointer',
    borderRadius: '4px',
  },
  editContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  dateInput: {
    fontSize: '14px',
    padding: '5px',
    borderRadius: '4px',
  },
  confirmButton: {
    backgroundColor: '#62fbfa',
    border: 'none',
    padding: '5px',
    cursor: 'pointer',
    borderRadius: '4px',
  },
};

export default React.memo(SongItem);
